import { LitElement, html, css } from 'lit';
import {customElement, property, query} from 'lit/decorators.js'
import GridCSS from './styles/grid'
import '@shoelace-style/shoelace/dist/components/button/button'
import '@shoelace-style/shoelace/dist/components/tab/tab'
import '@shoelace-style/shoelace/dist/components/tab-group/tab-group'
import '@shoelace-style/shoelace/dist/components/tab-panel/tab-panel'
import '@shoelace-style/shoelace/dist/components/copy-button/copy-button'
import './vxc_websrc_editor'
import './vxc_chatbot_kpi'
import './vxc_input'
import './vxc_textarea'
import './vxc_select'
import './vxc_input_tags'
import './vxc_knowledge_base'
import './vxc_chatbot'
import { TInput } from './vxc_input';

const LANGUAGES = [
  { label: "English", value: "English" },
  { label: "French", value: "French" },
  { label: "German", value: "German" },
  { label: "Spanish", value: "Spanish" },
  { label: "Italian", value: "Italian" },
  { label: "Portuguese", value: "Portuguese" },
  { label: "Dutch", value: "Dutch" },
  { label: "Russian", value: "Russian" },
  { label: "Chinese (Traditional)", value: "Chinese" },
  { label: "Japanese", value: "Japanese" },
  { label: "Korean", value: "Korean" },
  { label: "Arabic", value: "Arabic" },
  { label: "Turkish", value: "Turkish" },
  { label: "Hindi", value: "Hindi" },
  { label: "Bengali", value: "Bengali" },
  { label: "Polish", value: "Polish" },
  { label: "Greek", value: "Greek" },
  { label: "Hebrew", value: "Hebrew" },
  { label: "Swedish", value: "Swedish" },
  { label: "Czech", value: "Czech" },
  { label: "Thai", value: "Thai" },
  { label: "Vietnamese", value: "Vietnamese" },
  { label: "Indonesian", value: "Indonesian" },
  { label: "Romanian", value: "Romanian" },
  { label: "Hungarian", value: "Hungarian" },
  { label: "Ukrainian", value: "Ukrainian" },
  { label: "Finnish", value: "Finnish" },
  { label: "Malay", value: "Malay" },
  { label: "Norwegian", value: "Norwegian" },
  { label: "Slovak", value: "Slovak" },
  { label: "Danish", value: "Danish" },
  { label: "Tagalog", value: "Tagalog" },
  { label: "Tamil", value: "Tamil" },
  { label: "Urdu", value: "Urdu" },
  { label: "Swahili", value: "Swahili" },
  { label: "Gujarati", value: "Gujarati" },
  { label: "Kannada", value: "Kannada" },
  { label: "Amharic", value: "Amharic" },
  { label: "Zulu", value: "Zulu" },
  { label: "Xhosa", value: "Xhosa" },
  { label: "Burmese", value: "Burmese" },
  { label: "Khmer", value: "Khmer" },
  { label: "Lao", value: "Lao" },
  { label: "Pashto", value: "Pashto" },
  { label: "Nepali", value: "Nepali" }
]

interface Application {
    name: string
    context: string                
    dialog: {
        intro: { text: string }           
        noans: { text: string }           
    }
    agent: {
        name: string            
        role: string            
        lang: string            
    }
    source: {
        limit?: number
        rerank?: number
        engines?: string
        threshold?: number
        categories?: string
        domain: {
            name: string 
            include?: string[]
            exclude?: string[]
        }
    }
}

interface Document {
    uid: string
    last_updated: Date|string
    title: string
    description: string 
    content: string 
    status?: string
}

@customElement('vxc-chatbot-editor')
class TChatBotEditor extends LitElement {
  static styles = [
    GridCSS,
    css`
        :host {
            width: 100%;
            font-family: 'Poppins', sans-serif;
            background-color: #f6f9ff;
        }

        .main-view {
            background-color: white;
            boder: 1px solid #CCC;
            box-shadow: rgba(1, 41, 112, 0.1) 0px 0px 30px 0px;
            -webkit-border-radius: 5px;
            border-radius: 5px;
            overflow: auto;
            margin-too: 1rem;
            margin-bottom: 1rem;
        }

        h1 {
            font-size: 1.1rem;
        }

        h2 {
            font-size: .8rem;
            margin-left: 1.5rem;
        }
        .container {
            overflow: auto;
        }

        .button-set {
            display: flex; 
            justify-content: flex-end; 
            gap: 1rem;
            margin: 1rem 2rem;
        }
    `]

    @property({ type: String, attribute: 'app-uid'   })      uid : string = ''
    @property({ type: Object, attribute: false       })      app?: Application
    @property({ type: Array, attribute: false        })      docs?: Array<Document>

    @query('#app-name')     appNameInput!:TInput
    @query('#agent-name')   agentNameInput!:TInput
    @query('#agent-role')   agentRoleInput!:TInput
    @query('#welcome-msg')  welcomeMsgInput!:TInput
    @query('#noanswer-msg') errorMsgInput!:TInput


	private async read() {
		try {
			console.log('loading app..', `/apps/${this.uid}`)
            if(!window.voxyapp) throw new Error('voxy app not loaded')
			if(!this.uid)  throw new Error('no app to load')
            await this.readApp()
            await this.readDocs()
		} 
		catch(e) { console.error('Failed to load application', e) }
	}

	private async readApp() {
		try {
			console.log('loading app..', `/apps/${this.uid}`)
			if(!this.uid)  throw new Error('no app to load')
            if(!window.voxyapp) throw new Error('voxy app not loaded')
			const data = await window.voxyapp.get(`/apps/${this.uid}`)
			console.log('loaded app', data)
			if(!data) throw new Error('no stats found')
            data.source = data.source || { domain: { name: '', include: [], exclude: [] } }
            console.log('read app data', data)
			this.app = data
		} 
		catch(e) { console.error('Failed to load application', e) }
	}

    private async readDocs() {
		try {
			console.log('loading app..', `/apps/${this.uid}`)
			if(!this.uid)  throw new Error('no app to load')
            if(!window.voxyapp) throw new Error('voxy app not loaded')
			const data = await window.voxyapp.get(`/apps/${this.uid}/docs`)
			console.log('docs', data)
			if(!data) throw new Error('no stats found')
            this.docs = data
		} 
		catch(e) { console.error('Failed to load docs', e) }
	}


    private getApp() : Application {
        this.app = this.app || { 
            name: '',    
            context: '',            
            dialog: {
                intro: { text: '' },          
                noans: { text: '' }           
            },
            agent: {
                name: '',            
                role: '',            
                lang: ''            
            },
            source: {
                domain: {
                    name: '', 
                    include: [],
                    exclude: []
                }
            }
        }
        console.log('get App :', this.app)
        return this.app
    }

    private updateAppName(event: CustomEvent) {
        try {
            console.log('update nae event :',   event)
            let input = event.detail
            if(!input)  throw new Error('no input in event details')
            let app = this.getApp()
            app.name = input.value 
            this.app = app
            console.log('app name set to', this.app.name)
        }
        catch(e) { console.error('Error updating domain', e) }
    }

    private updateAgentName(event: CustomEvent) {
        try {
            let input = event.detail
            if(!input)  throw new Error('no input in event details')
            let app = this.getApp()
            app.agent.name = input.value 
            this.app = app
            console.log('agent name set to', this.app.agent.name)
        }
        catch(e) { console.error('Error updating domain', e) }
    }

    private updateAgentRole(event: CustomEvent) {
        try {
            let input = event.detail
            if(!input)  throw new Error('no input in event details')
            let app = this.getApp()
            app.agent.role = input.value 
            this.app = app
            console.log('agent role set to', this.app.agent.role)
        }
        catch(e) { console.error('Error updating domain', e) }
    }

    private updateWelcomeMsg(event: CustomEvent) {
        try {
            let input = event.detail
            if(!input)  throw new Error('no input in event details')
            let app = this.getApp()
            app.dialog.intro.text = input.value 
            this.app = app
            console.log('welcome message set to', this.app.dialog.intro.text)
        }
        catch(e) { console.error('Error updating domain', e) }
    }

    private updateErrorMsg(event: CustomEvent) {
        try {
            let input = event.detail
            if(!input)  throw new Error('no input in event details')
            let app = this.getApp()
            app.dialog.noans.text = input.value 
            this.app = app
            console.log('error message set to', this.app.dialog.noans.text)
        }
        catch(e) { console.error('Error updating domain', e) }
    }

    private updateContext(event: CustomEvent) {
        try {
            let input = event.detail
            if(!input)  throw new Error('no input in event details')
            let app = this.getApp()
            app.context = input.value 
            this.app = app
            console.log('context set to', this.app.context)
        }
        catch(e) { console.error('Error updating domain', e) }
    }

    private updateLanguage(event: CustomEvent) {
        try {
            let input = event.detail
            if(!input)  throw new Error('no input in event details')
            let app = this.getApp()
            app.agent.lang = input.value 
            this.app = app
            console.log('agent language set to', this.app.agent.lang)
        }
        catch(e) { console.error('Error updating domain', e) }
    }

    private updateDomain(event: CustomEvent) {
        try {
            let input = event.detail
            if(!input)  throw new Error('no input in event details')
            let app = this.getApp()
            app.source.domain.name = input.value 
            this.app = app
            console.log('domain set to', this.app.source.domain.name)
        }
        catch(e) { console.error('Error updating domain', e) }
    }

    private updateInclude(event: CustomEvent) {
        try {
            let input = event.detail
            if(!input)  throw new Error('no input in event details')
            let app = this.getApp()
            app.source.domain.include = input.value 
            this.app = app
            console.log('include url list set to', this.app.source.domain.include)
        }
        catch(e) { console.error('Error updating domain', e) }
    }

    private updateExclude(event: CustomEvent) {
        try {
            let input = event.detail
            if(!input)  throw new Error('no input in event details')
            let app = this.getApp()
            app.source.domain.exclude = input.value 
            this.app = app
            console.log('exclude url list set to', this.app.source.domain.exclude)
        }
        catch(e) { console.error('Error updating domain', e) }
    }

    private updateDocs(event: CustomEvent) {
        try {
            let input = event.detail
            if(!input)  throw new Error('no input in event details')
            this.docs = input. value
            console.log('docs updated') 
        }
        catch(e) { console.error('Error updating docs', e) }
    }

    private validateInput() : boolean {
        var valid = false 
        try {
            valid = this.appNameInput.isValid()
                &&  this.agentNameInput.isValid()
                &&  this.agentRoleInput.isValid()
                &&  this.welcomeMsgInput.isValid()
                &&  this.errorMsgInput.isValid()
        }
        catch(e) { console.error('error validating inputs', e) }
        finally  { return valid }
    }

    private async save(event: Event) {
        try {
            event.preventDefault()
            if(!window.voxyapp) throw new Error('voxy app not loaded')
            if(!this.validateInput())   return
            await this.saveApp()
            await this.saveDocs()
            console.log('app saved..')
            this.dispatchEvent(new CustomEvent('app-updated', { detail: { value: this.app }, bubbles: true, composed: true }))
        }
        catch(e) { console.error(e) }
    }

        private async saveApp() {
        try {
            var payload = { type: 'chat.simple', settings: this.app }
            var route   = (this.uid) ?  `/apps/${this.uid}` : '/apps'
            var resp    = await window.voxyapp.post(route, payload)
            console.log('payload', payload)
			console.log('save', resp)
            console.log('app uid = ', resp.uid)
			if(!resp) throw new Error('app was not saved')
            console.log('app saved..')
            this.uid = this.uid || resp.uid
            this.dispatchEvent(new CustomEvent('done', { detail: { value: this.app } }))
        }
        catch(e) { console.error(e) }
    }

        private async saveDocs() {
        try {
            let count = 0
            for(const doc of this.docs || []) {
                var resp = null
                var payload = doc
                var route   = `/apps/${this.uid}/docs/${doc.uid}`
                switch(doc.status) {
                    case 'created':
                    case 'updated':
                        resp = await window.voxyapp.post(route, payload)
                        if(!resp)   console.error('error updating doc', doc)
                        else count ++
                        break
                    case 'deleted':
                        resp = await window.voxyapp.delete(route)
                        if(!resp)   console.error('error deleting doc', doc)
                        else count ++
                        break
                }
            }
            console.log(count, 'documents updated..')
        }
        catch(e) { console.error(e) }
    }

    private cancel(event: Event) {
        try {
            event.preventDefault()
            this.dispatchEvent(new CustomEvent('done', { detail: { value: this.app } }))
        }
        catch(e) { console.error(e) }
    }

    updated(changedProperties: Map<string | number | symbol, unknown>): void {
        if (changedProperties.has('uid') && this.uid?.length) {
            console.log(`'app uid' changed from ${changedProperties.get('uid')} to ${this.uid}`);
            this.read()
        }
        super.updated(changedProperties)
    }

    render() {
        return html`
                <div class="container-fluid main-view">
                    <div class="row">
                        <div class="col-12 col-md-6">
                            <h1>ChatBot Application</h1>
                            <vxc-input 
                                id="app-name"
                                title="Application name" 
                                label="Give a friendly identifier to your application."
                                value=${this.app?.name||''}
                                @valueUpdated=${this.updateAppName}
                                required
                            ></vxc-input>
                        </div>
                    </div>                                       

                    <div class="row">
                        <div class="col-12 col-md-6">
                            <vxc-input 
                                id="agent-name"
                                title="Agent name" 
                                label="Give a nice name to your BOT."
                                value=${this.app?.agent?.name||''}
                                @valueUpdated=${this.updateAgentName}
                                required
                            ></vxc-input>
                        </div>
                        <div class="col-12 col-md-6">
                            <vxc-select 
                                title="language" 
                                label="Select the language of you BOT"
                                options=${JSON.stringify(LANGUAGES)}
                                value=${this.app?.agent?.lang||'English'}
                                @valueUpdated=${this.updateLanguage}
                                required
                            ></vxc-select>
                        </div>
                        <div class="col-12">
                            <vxc-textarea
                                id="context"
                                title="context" 
                                label="Describe the context of the app"
                                value=${this.app?.context||''}
                                @valueUpdated=${this.updateContext}
                                placeholder="context.."
                            ></vxc-textarea>
                        </div>
                        <div class="col-12">
                            <vxc-input
                                id="agent-role"
                                title="role" 
                                label="Select the role you BOT"
                                value=${this.app?.agent?.role||''}
                                @valueUpdated=${this.updateAgentRole}
                                placeholder="a helpful assistant with a lot of knowledge about my company."
                                required
                            ></vxc-input>
                        </div>

                    </div>       
                    
                    <div class="row">
                        <div class="col-12">
                            <vxc-input 
                                id="welcome-msg"
                                title="Welcome message" 
                                label="the text the BOT starts the conversation with."
                                value=${this.app?.dialog?.intro?.text||''}
                                @valueUpdated=${this.updateWelcomeMsg}
                                required
                            ></vxc-input>
                        </div>
                        <div class="col-12">
                            <vxc-input 
                                id="noanswer-msg"
                                title="Error message" 
                                label="the BOT response if he does not find the answer to the user question."
                                value=${this.app?.dialog?.noans?.text||''}
                                @valueUpdated=${this.updateErrorMsg}
                                required
                            ></vxc-input>
                        </div>
                    </div>    
                </div>

                ${ this.uid ? html`
                    <div class="container-fluid main-view">
                    <sl-tab-group>
                        <sl-tab slot="nav" panel="kbz">Knowledge Base</sl-tab>
                        <sl-tab slot="nav" panel="dmo">Demo link</sl-tab>
                        <sl-tab slot="nav" panel="kpi">Operational KPI</sl-tab>

                        <sl-tab-panel name="kbz">
                            <vxc-websrc-editor
                                domain=${this.app?.source?.domain?.name||''}
                                include=${JSON.stringify(this.app?.source?.domain?.include||[])}
                                exclude=${JSON.stringify(this.app?.source?.domain?.exclude||[])}
                                @domain-updated=${this.updateDomain}
                                @include-updated=${this.updateInclude}
                                @exclude-updated=${this.updateExclude}
                            ></vxc-websrc-editor>
                            <vxc-knowledge-base
                                title="Manually edited Knowledge Base"
                                data=${JSON.stringify(this.docs)}
                                @updated=${this.updateDocs}
                            ></vxc-knowledge-base>
                        </sl-tab-panel>

                        <sl-tab-panel name="dmo">
                            <sl-input id="demo-link" type="text" value="https://preprod.iamie.fr/chat/${this.uid}" style="display: inline-block; width: 80%; min-width: 10rem;" disabled readonly></sl-input>
                            <sl-copy-button from="demo-link.value"></sl-copy-button>
                        </sl-tab-panel>

                        <sl-tab-panel name="kpi">
                            <vxc-chatbot-kpi
                                app-uid=${this.uid}
                            ></vxc-chatbot-kpi>
                        </sl-tab-panel>

                        <vxc-chatbot
                            app=${this.uid}
                            host="https://app.jinai.ai"
                            data={}
                        ></vxc-chatbot>

                    </sl-tab-group>
                </div>` : '' }

                

                <div class="container-fluid main-view">
                    <div class="button-set">
                        <sl-button variant="neutral" @click=${this.cancel} outline>Cancel</sl-button>
                        <sl-button variant="neutral" @click=${this.save}>Save Application</sl-button>
                    </div>
                </div>
            </div>
        `;
    }
}

declare global {
  interface HTMLElementTagNameMap {
    'vxc-chatbot-editor': TChatBotEditor;
  }
}